import React, { useEffect, useMemo, useState } from 'react';
import {
  Target,
  Flame,
  Timer,
  Trophy,
  Pencil,
  Loader2,
  Coins,
  LayoutGrid,
  FileText,
  ChevronRight,
} from 'lucide-react';
import { api, type Profile, type HistoryEntry, type QuizSummary } from '../lib/api';
import { BRAND } from '../brand';
import { FavoriteQuizzes } from './FavoriteQuizzes';
import { EditProfileModal } from './EditProfileModal';
import { ActivityList } from './ActivityList';
import { HostFilter } from './HostFilter';
import { RankingPositions } from './RankingPositions';
import { ProfileHero, StatTile } from './ProfileHero';

const TYPE_LABELS: Record<string, string> = {
  multiple_choice: 'Opción múltiple',
  true_false: 'Verdadero / Falso',
  multi_select: 'Varias correctas',
  fill_blank: 'Respuesta escrita',
  match_pairs: 'Emparejar',
};

export const AnalyticsView: React.FC<{
  profile: Profile;
  onProfileChange: (p: Profile) => void;
  /** Llega en `true` cuando se entró aquí para configurar la clave de IA. */
  abrirEnIa?: boolean;
  onAbierto?: () => void;
  onHostQuiz: (quiz: QuizSummary) => void;
  /** Abre «Mis trivias y mis exámenes». */
  onOpenMine: () => void;
  onStudyQuiz: (quizId: string) => void;
  favoriteIds: Set<string>;
  onToggleFavorite: (quizId: string) => void;
  /** La cuenta se borró desde el editor del perfil. */
  onAccountDeleted: () => void;
}> = ({
  profile,
  onProfileChange,
  abrirEnIa,
  onAbierto,
  onHostQuiz,
  onOpenMine,
  onStudyQuiz,
  favoriteIds,
  onToggleFavorite,
  onAccountDeleted,
}) => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  // La pestaña con la que abrir el editor se guarda aquí en vez de leerse de
  // `abrirEnIa` al pintar: avisar al padre lo pone a false en el mismo ciclo,
  // así que al montarse el modal ya no quedaba rastro de la intención.
  const [pestanaEditor, setPestanaEditor] = useState<'ia' | undefined>(undefined);

  // Entrar desde "crear con IA" abre el editor directamente en su pestaña.
  useEffect(() => {
    if (abrirEnIa) {
      setPestanaEditor('ia');
      setEditing(true);
      onAbierto?.();
    }
  }, [abrirEnIa, onAbierto]);

  // Filtro de anfitrión: las partidas jugadas en la propia sala no cuentan en
  // las estadísticas oficiales, pero se pueden ver sumadas.
  const hostPlayed = profile.withHost?.hostPlayedGames ?? 0;
  const [conAnfitrion, setConAnfitrion] = useState(false);
  const stats = conAnfitrion && profile.withHost ? profile.withHost : profile;


  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ history: HistoryEntry[] }>('/profile/history');
        setHistory(data.history);
      } catch {
        setHistory([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /** Punto débil: el tipo de pregunta con peor acierto, si hay datos suficientes. */
  const weakest = useMemo(() => {
    const entries = Object.entries(profile.accuracyByType ?? {})
      .filter(([, v]) => v && v.total >= 4)
      .map(([type, v]) => ({ type, rate: v.correct / v.total, total: v.total }));
    if (entries.length < 2) return null;
    return entries.sort((a, b) => a.rate - b.rate)[0];
  }, [profile.accuracyByType]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      {/* Identidad */}
      <ProfileHero
        avatar={profile.avatar}
        username={profile.username}
        details={[profile.countryName, profile.age !== null ? `${profile.age} años` : null]}
        dRating={stats.dRating}
        ratingLabel={conAnfitrion ? 'D-Rating con anfitrión' : 'D-Rating'}
        actions={
          // Con texto y no solo un lápiz: antes nadie encontraba dónde se
          // cambiaban los datos de la cuenta.
          <button
            onClick={() => setEditing(true)}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 h-11 rounded-lg
                       font-semibold text-sm bg-ink-800 hover:bg-ink-700 border border-ink-600
                       text-ink-100 transition-colors"
          >
            <Pencil className="w-4 h-4" />
            Editar perfil
          </button>
        }
      />

      {/* Filtro de anfitrión */}
      {hostPlayed > 0 && (
        <HostFilter
          hostPlayedGames={hostPlayed}
          verbo="Jugaste"
          checked={conAnfitrion}
          onChange={setConAnfitrion}
        />
      )}

      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          tone="ember"
          icon={<Trophy className="w-5 h-5" />}
          label="Victorias"
          value={`${stats.wins}`}
          sub={`de ${stats.totalGames} partidas`}
        />
        <StatTile
          tone="success"
          icon={<Target className="w-5 h-5" />}
          label="Aciertos"
          value={`${Math.round(stats.accuracy * 100)}%`}
          sub={`${stats.correctAnswers} de ${stats.totalAnswers}`}
        />
        <StatTile
          tone="magenta"
          icon={<Flame className="w-5 h-5" />}
          label="Mejor racha"
          value={`${stats.bestStreak}`}
        />
        <StatTile
          tone="sky"
          icon={<Timer className="w-5 h-5" />}
          label="Tiempo medio"
          value={
            stats.avgResponseTimeMs > 0
              ? `${(stats.avgResponseTimeMs / 1000).toFixed(1)}s`
              : '—'
          }
        />
      </div>

      <RankingPositions userId={profile.id} own refreshKey={`${profile.dRating}-${profile.dahus}`} />

      {/* Punto débil */}
      {weakest && (
        <div className="surface p-5">
          <p className="text-ink-400 text-xs uppercase tracking-wider mb-1.5">Dónde flojeas</p>
          <p className="text-ink-100">
            Tu peor tipo es{' '}
            <span className="font-semibold">{TYPE_LABELS[weakest.type] ?? weakest.type}</span>:{' '}
            {Math.round(weakest.rate * 100)}% de acierto en {weakest.total} respuestas.
          </p>
        </div>
      )}

      {/* Saldo */}
      <div className="surface p-5 flex items-center gap-4 bg-gradient-to-r from-ember-500/10 to-transparent">
        <span className="w-12 h-12 rounded-2xl bg-ember-500/15 text-ember-400 flex items-center justify-center shrink-0">
          <Coins className="w-6 h-6" />
        </span>
        <div className="flex-1">
          <p className="text-ink-400 text-sm">{BRAND.currencyName} disponibles</p>
          <p className="text-2xl font-bold font-mono tabular-nums text-ember-300">
            {profile.dahus.toLocaleString('es')}
          </p>
        </div>
      </div>

      {/* Lo creado vive en su propia sección: aquí solo la puerta, para que el
          perfil no cargue con dos listas largas. */}
      <button
        onClick={onOpenMine}
        className="surface p-5 w-full flex items-center gap-4 text-left hover:border-sky-500/60 transition-colors"
      >
        <span className="flex -space-x-2 shrink-0">
          <span className="w-11 h-11 rounded-2xl bg-ember-500/15 text-ember-400 flex items-center justify-center">
            <LayoutGrid className="w-5 h-5" />
          </span>
          <span className="w-11 h-11 rounded-2xl bg-sky-500/15 text-sky-400 flex items-center justify-center">
            <FileText className="w-5 h-5" />
          </span>
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-semibold text-ink-50">Mis trivias y mis exámenes</span>
          <span className="block text-ink-400 text-sm">Edita, organiza en carpetas, abre exámenes y mira las notas</span>
        </span>
        <ChevronRight className="w-5 h-5 text-ink-500 shrink-0" />
      </button>

      {/* Trivias marcadas con el corazón */}
      <FavoriteQuizzes
        favoriteIds={favoriteIds}
        onToggleFavorite={onToggleFavorite}
        onHost={onHostQuiz}
        onStudy={onStudyQuiz}
      />

      {/* Historial */}
      <div className="surface p-5">
        <h2 className="font-semibold mb-3">Actividad</h2>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
          </div>
        ) : (
          <ActivityList history={history} emptyText="Todavía no has jugado ninguna partida en vivo." />
        )}
      </div>

      {editing && (
        <EditProfileModal
          profile={profile}
          pestanaInicial={pestanaEditor}
          onProfileChange={onProfileChange}
          onAccountDeleted={onAccountDeleted}
          onClose={() => {
            setEditing(false);
            setPestanaEditor(undefined);
          }}
        />
      )}
    </div>
  );
};
